import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useScanner } from "@/context/ScannerContext";
import { mfs100Capture, rdCapture, SCANNER_STATE, MFS100_ERRORS } from "@/lib/mantra";
import { StatePill } from "./ScannerStatus";
import { QualityBar } from "./EnhanceDialog";

export function useCaptureFlow() {
  const { status, settings, refresh } = useScanner();
  const [phase, setPhase] = useState(SCANNER_STATE.READY);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const reset = () => { setPhase(SCANNER_STATE.READY); setProgress(0); setResult(null); setError(""); };

  const capture = async () => {
    setError(""); setResult(null);
    const live = await refresh();
    if (!live || live.state !== SCANNER_STATE.CONNECTED) {
      setPhase(live?.state || SCANNER_STATE.NOT_FOUND);
      setError(live?.message || "Mantra scanner not available.");
      return null;
    }
    setPhase(SCANNER_STATE.CAPTURING);
    const timeout = settings?.capture_timeout || 10;
    let pct = 0;
    const timer = setInterval(() => { pct = Math.min(95, pct + 100 / (timeout * 4)); setProgress(pct); }, 250);
    try {
      let out;
      if (live.mode === "mfs100_client") {
        const r = await mfs100Capture(live.client.base, settings?.min_quality || 60, timeout);
        if (String(r.ErrorCode) !== "0") throw new Error(MFS100_ERRORS[String(r.ErrorCode)] || r.ErrorDescription || "Capture failed");
        out = { mode: "mfs100_client", base: live.client.base, template: r.IsoTemplate, image: r.BitmapData ? `data:image/bmp;base64,${r.BitmapData}` : null,
          quality: Number(r.Quality) || null, nfiq: Number(r.Nfiq) || null,
          device: { model: `${live.client.Make || "Mantra"} ${live.client.Model || ""}`.trim(), serial: live.client.SerialNo || null, mode: "mfs100_client" } };
      } else {
        const r = await rdCapture(live.rd, timeout);
        if (r.errCode !== "0") throw new Error(r.errInfo || `RD capture failed (code ${r.errCode})`);
        out = { mode: "rd_service", template: null, image: null, quality: Number(r.qScore) || null, nfiq: null, rdOnly: true,
          rdMeta: { fCount: r.fCount, nmPoints: r.nmPoints, qScore: r.qScore, rdsVer: live.rdInfo?.rdsVer, dpId: live.rdInfo?.dpId, mi: live.rdInfo?.mi },
          device: { model: `${settings?.device_model || "Mantra MFS110 L1"} (${live.rdInfo?.mi || "RD"})`, serial: live.rdInfo?.dc || null, mode: "rd_service" } };
      }
      clearInterval(timer); setProgress(100); setPhase(SCANNER_STATE.SUCCESS); setResult(out);
      return out;
    } catch (e) {
      clearInterval(timer); setProgress(0); setPhase(SCANNER_STATE.FAILED);
      setError(e.name === "AbortError" ? "Capture timed out — finger not detected." : e.message);
      return null;
    }
  };
  return { status, phase, progress, result, error, capture, reset, settings };
}

export default function CaptureModal({ open, onClose, candidate, finger: initialFinger, existing, onSaved }) {
  const [finger, setFinger] = useState(String(initialFinger || 1));
  const [saving, setSaving] = useState(false);
  const flow = useCaptureFlow();
  const taken = candidate?.fingers || {};

  useEffect(() => { if (open) { setFinger(String(initialFinger || 1)); flow.reset(); } }, [open, initialFinger]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!flow.result) return;
    setSaving(true);
    try {
      await api.post("/attendance/fingerprints", {
        candidate_id: candidate.id, finger_position: Number(finger), template: flow.result.template, image: flow.result.image,
        quality_score: flow.result.quality, nfiq: flow.result.nfiq, device: flow.result.device, rd_meta: flow.result.rdMeta || null, recapture: !!taken[finger],
      });
      toast.success(taken[finger] ? "Fingerprint re-captured" : "Fingerprint saved");
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!candidate) return null;
  const isDup = !!taken[finger] && !existing;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="capture-modal" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Capture Fingerprint</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-xs text-slate-500">Candidate Name</p><p className="font-semibold text-slate-900" data-testid="capture-candidate-name">{candidate.name}</p></div>
          <div><p className="text-xs text-slate-500">Candidate ID</p><p className="font-semibold text-slate-900">{candidate.candidate_code}</p></div>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Select Finger</p>
          <Select value={finger} onValueChange={(v) => { setFinger(v); flow.reset(); }}>
            <SelectTrigger data-testid="capture-finger-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>Finger {n}{taken[n] ? " (captured)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
          <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Scanner</span><span className="text-sm font-medium">{flow.settings?.device_model || "Mantra MFS110 L1"}</span></div>
          <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Status</span><StatePill state={flow.phase === SCANNER_STATE.READY ? flow.status.state : flow.phase} testid="capture-state" /></div>
          {flow.phase === SCANNER_STATE.CAPTURING && (<><p className="text-sm text-emerald-800 font-medium text-center pt-2">Place your finger on the Mantra scanner</p><Progress value={flow.progress} className="h-2" /></>)}
          {flow.error && <p data-testid="capture-error" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{flow.error}</p>}
          {flow.result && (
            <div className="text-center pt-2" data-testid="capture-result">
              {flow.result.image ? <img src={flow.result.image} alt={`Captured fingerprint preview, finger ${finger}`} className="w-28 h-36 object-contain mx-auto rounded-lg border bg-white" /> :
                <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">RD Service capture successful (encrypted PID, {flow.result.rdMeta?.nmPoints || "—"} minutiae points). The MFS110 L1 device does not release the fingerprint image/template to applications, so no image can be shown or matched locally. Quality & device details are recorded.</p>}
              <p className="font-semibold text-emerald-800 mt-2">Capture Successful</p>
              <div className="mt-2 max-w-xs mx-auto"><QualityBar quality={flow.result.quality} /></div>
            </div>
          )}
        </div>
        {isDup && !flow.result && <p data-testid="duplicate-warning" className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">This fingerprint is already captured. Re-capture will replace it.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" data-testid="capture-cancel" onClick={onClose} className="rounded-xl">Cancel</Button>
          {flow.result ? (<>
            <Button variant="outline" data-testid="capture-retry" onClick={flow.capture} className="rounded-xl">Re-Capture</Button>
            <Button data-testid="capture-save" onClick={save} disabled={saving} className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white">{saving ? "Saving…" : "Save"}</Button>
          </>) : (
            <Button data-testid="capture-start" onClick={flow.capture} disabled={flow.phase === SCANNER_STATE.CAPTURING} className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white">{isDup ? "Re-Capture" : "Capture Fingerprint"}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
